import {
  buildDocumentSlotDownloadName,
  buildDocumentSlotPath,
  buildDocumentSlotVersionPath,
  validateDocumentSlotFile,
  MAX_STORED_FILE_BYTES,
} from "../documents/document-slots";
import { DOCUMENT_CATALOG, type DocumentTypeFor } from "../documents/document-catalog";
import { unknownOrderDocumentTypeMessage } from "./order-policy-copy";

const ENTITY_SEGMENT = "orders";

export type OrderDocumentType = DocumentTypeFor<"order">;

export const ORDER_DOCUMENT_TYPES = DOCUMENT_CATALOG.list("order").map((entry) => entry.key);

export const ORDER_DOCUMENT_TYPES_META = Object.fromEntries(
  DOCUMENT_CATALOG.list("order").map((entry) => [entry.key, entry]),
) as Record<OrderDocumentType, ReturnType<typeof DOCUMENT_CATALOG.list<"order">>[number]>;

export const ORDER_DOCUMENT_TYPE_LABELS: Record<OrderDocumentType, string> = Object.fromEntries(
  ORDER_DOCUMENT_TYPES.map((documentType) => [documentType, ORDER_DOCUMENT_TYPES_META[documentType].label]),
) as Record<OrderDocumentType, string>;

export function isOrderDocumentType(value: string): value is OrderDocumentType {
  return value in ORDER_DOCUMENT_TYPES_META;
}

export function buildOrderDocumentPath(params: {
  tenantId: number;
  orderId: number;
  documentType: OrderDocumentType;
}): string {
  const { tenantId, orderId, documentType } = params;
  const meta = ORDER_DOCUMENT_TYPES_META[documentType];
  if (!meta) {
    throw new Error(unknownOrderDocumentTypeMessage(documentType));
  }
  return buildDocumentSlotPath({ tenantId, entitySegment: ENTITY_SEGMENT, entityId: orderId, filename: meta.filename });
}

export function buildOrderDocumentVersionPath(params: {
  tenantId: number;
  orderId: number;
  documentType: OrderDocumentType;
  versionId: string;
}): string {
  const { tenantId, orderId, documentType, versionId } = params;
  if (!isOrderDocumentType(documentType)) {
    throw new Error(unknownOrderDocumentTypeMessage(documentType));
  }
  return buildDocumentSlotVersionPath({
    tenantId,
    entitySegment: ENTITY_SEGMENT,
    entityId: orderId,
    documentType,
    versionId,
    extension: "pdf",
  });
}

export function buildOrderDocumentDownloadName(params: {
  referenceNumber: string | null | undefined;
  documentType: OrderDocumentType;
}): string {
  const { referenceNumber, documentType } = params;
  const suffix = ORDER_DOCUMENT_TYPES_META[documentType].downloadSuffix;
  return buildDocumentSlotDownloadName({ identifier: referenceNumber, suffix, extension: "pdf" });
}

export const ORDER_DOCUMENT_MIME_TYPE = "application/pdf";
export const MAX_ORDER_DOCUMENT_BYTES = MAX_STORED_FILE_BYTES;

export type OrderDocumentFileInput = {
  filename: string;
  mimeType: string;
  size: number;
};

export type OrderDocumentFileValidation =
  | { ok: true }
  | { ok: false; reason: "invalid_extension" | "invalid_mime_type" | "file_too_large" };

export function validateOrderDocumentFile(file: OrderDocumentFileInput): OrderDocumentFileValidation {
  return validateDocumentSlotFile(file, {
    extension: "pdf",
    mimeType: ORDER_DOCUMENT_MIME_TYPE,
    maxBytes: MAX_ORDER_DOCUMENT_BYTES,
  });
}
